/**
 * Type definitions for LLM Provider Settings
 * These types match the backend API structure for LLM provider configuration
 */

export enum LLMProvider {
  AZURE_OPENAI = 'azure_openai',
  OPENAI = 'openai',
  ANTHROPIC = 'anthropic',
  OLLAMA = 'ollama'
}

// Base configuration shared by all providers
export interface BaseLLMConfig {
  provider: LLMProvider;
  displayName: string;
  model: string;
  temperature?: number;
  maxTokens?: number;
  timeoutSeconds?: number;
}

// Azure OpenAI specific configuration
export interface AzureOpenAIConfig extends BaseLLMConfig {
  provider: LLMProvider.AZURE_OPENAI;
  apiKey: string;
  endpoint: string;
  apiVersion?: string;
  deploymentName: string;
}

// OpenAI specific configuration
export interface OpenAIConfig extends BaseLLMConfig {
  provider: LLMProvider.OPENAI;
  apiKey: string;
  baseUrl?: string;
  organizationId?: string;
}

// Anthropic specific configuration
export interface AnthropicConfig extends BaseLLMConfig {
  provider: LLMProvider.ANTHROPIC;
  apiKey: string;
}

// Ollama specific configuration (local models)
export interface OllamaConfig extends BaseLLMConfig {
  provider: LLMProvider.OLLAMA;
  baseUrl?: string;
  model: string;
}

// Union type for all provider configurations
export type LLMProviderConfig = 
  | AzureOpenAIConfig 
  | OpenAIConfig 
  | AnthropicConfig 
  | OllamaConfig;

// Request type for saving provider configuration
export interface LLMProviderRequest {
  config: LLMProviderConfig;
  makeActive?: boolean;
}

// Response type for provider status endpoint
export interface LLMProviderStatus {
  configured: boolean;
  activeProvider?: LLMProvider;
  activeDisplayName?: string;
  availableProviders: Array<{
    provider: string;
    displayName: string;
  }>;
  detailedProviders?: LLMProviderDetail[];
}


// Type for saved provider in the list
export interface SavedProvider {
  id: number;
  provider: LLMProvider;
  displayName: string;
  isActive: boolean;
  createdAt: string;
  timeoutSeconds?: number;
  testStatus: 'success' | 'failed' | 'untested';
  lastTested?: string;
}

// Response type for save provider endpoint
export interface SaveProviderResponse {
  status: string;
  provider_id: number;
  message: string;
  active?: boolean;
}

// Response type for activate/delete provider endpoints
export interface ProviderActionResponse {
  status: string;
  provider_id?: number;
  message: string;
}

// Response type for getting a provider configuration
export interface LLMProviderConfigResponse {
  config: LLMProviderConfig;
  testErrorMessage?: string;
}

// Detailed provider information with decrypted config (matches backend LLMProviderDetail)
export interface LLMProviderDetail {
  id: number;
  provider: LLMProvider;
  displayName: string;
  config: LLMProviderConfig;  // The actual decrypted config
  isActive: boolean;
  testStatus: 'success' | 'failed' | 'untested';
  lastTested?: string;
  createdAt: string;
  updatedAt?: string;
  timeoutSeconds?: number;
}

// Type guard functions to help with type narrowing
export const isAzureOpenAIConfig = (config: LLMProviderConfig): config is AzureOpenAIConfig => {
  return config.provider === LLMProvider.AZURE_OPENAI;
};

export const isOpenAIConfig = (config: LLMProviderConfig): config is OpenAIConfig => {
  return config.provider === LLMProvider.OPENAI;
};

export const isAnthropicConfig = (config: LLMProviderConfig): config is AnthropicConfig => {
  return config.provider === LLMProvider.ANTHROPIC;
};

export const isOllamaConfig = (config: LLMProviderConfig): config is OllamaConfig => {
  return config.provider === LLMProvider.OLLAMA;
};

// Helper function to get display name for provider type
export const getProviderDisplayName = (provider: LLMProvider): string => {
  const names: Record<LLMProvider, string> = {
    [LLMProvider.AZURE_OPENAI]: 'Azure OpenAI',
    [LLMProvider.OPENAI]: 'OpenAI',
    [LLMProvider.ANTHROPIC]: 'Anthropic Claude',
    [LLMProvider.OLLAMA]: 'Ollama (Local)'
  };
  return names[provider] || provider;
};

// Default configuration values
export const DEFAULT_LLM_CONFIG = {
  temperature: 0.3,
  maxTokens: 4000,
  timeoutSeconds: 60
} as const;

// Validation helpers
export const validateProviderConfig = (config: Partial<LLMProviderConfig>): boolean => {
  if (!config.provider || !config.displayName || !config.model) {
    return false;
  }

  switch (config.provider) {
    case LLMProvider.AZURE_OPENAI:
      return !!(
        (config as AzureOpenAIConfig).apiKey &&
        (config as AzureOpenAIConfig).endpoint &&
        (config as AzureOpenAIConfig).deploymentName
      );
    
    case LLMProvider.OPENAI:
      return !!(config as OpenAIConfig).apiKey;
    
    case LLMProvider.ANTHROPIC:
      return !!(config as AnthropicConfig).apiKey;
    
    case LLMProvider.OLLAMA:
      // Ollama doesn't require API key, just needs base URL or uses default
      return true;
    
    default:
      return false;
  }
};